import { Image, StyleSheet, Text, View, TouchableOpacity } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'expo-router'
import { useThrottledCallback } from 'use-debounce'

import { GameModeTag, getInitials } from '@ppwr/poker'

import { color, fontFamily } from '../../../theme'
import { DrawerLabelValue } from '../DrawerLabelValue/DrawerLabelValue'
import { TableName } from '../TableName/TableName'
import CrownGradient from '../../../assets/crown-gradient.png'
import { translate } from '../../../i18n'
import { DS } from '../../../design-system'
import { useStore } from '@/models'
import { useIsInGame } from '@/hooks/useIsInGame'

import { useDrawerStore } from '@/components/Drawer/Drawer'

import { Avatar } from '@/screens/profile-screen/edit/AvatarBuilder'

const InGameHeaderItem = observer(() => {
  const store = useStore()
  const hash = store?.share?.info?.hash
  const isOpenTable = store.game?.isOpenTable ?? false
  const isPracticeModeOn = store.game?.isPracticeMode || store.game?.isPracticeTournamentMode
  const observersCount = store.game.nonSeatedObservers.length
  const isCommunityGame = store.game?.isCommunityGame ?? false
  const isCurrentGameATeachingTable = store.game?.isCurrentGameATeachingTable
  const tableName = store?.game?.tableName || 'Quick Game'
  const gameKind = store.game.gameKind

  const isObserversVisible = !isOpenTable && hash && !isPracticeModeOn && observersCount > 0

  const showShare = hash && !isCommunityGame && !isPracticeModeOn && !isOpenTable && !isCurrentGameATeachingTable

  const onObservers = useThrottledCallback(
    () => {
      store.game.createObserversSheet()
      useDrawerStore.getState().closeDrawer()
    },
    350,
    { leading: true }
  )

  const onShare = useThrottledCallback(
    async () => {
      if (hash) {
        useDrawerStore.getState().closeDrawer()
        store.share.setShare(hash)
        await store.share.promptGameShare()
      }
    },
    350,
    { leading: true }
  )
  /**
   * Defines game type labels based on GameModeTag.
   * This approach is more maintainable as adding new game types only requires
   * updating this object and the corresponding translation keys.
   */
  const GAME_TYPE = {
    // Translation keys for game types
    TRANSLATION: {
      // Game mode specific translation keys
      GAME_MODES: {
        [GameModeTag.default]: 'gameKind.quickGame',
        [GameModeTag.openQuickGame]: 'gameKind.quickGame',
        [GameModeTag.practice]: 'gameKind.practice',
        [GameModeTag.practiceTournament]: 'gameKind.practice',
        [GameModeTag.tournament]: 'gameKind.tournament'
      } as Record<string, string>,
      // Special condition translation keys
      COMMUNITY: 'gameKind.communityGame',
      OPEN_TABLE: 'gameKind.openTable'
    },
    // Display names for table types
    TABLE_NAME: {
      [GameModeTag.default]: 'Quick Game',
      [GameModeTag.openQuickGame]: 'Quick Game',
      [GameModeTag.practice]: 'Quick Game',
      [GameModeTag.practiceTournament]: 'Tournament',
      [GameModeTag.tournament]: 'Tournament'
    } as Record<string, string>
  }

  /**
   * Gets the appropriate game kind label based on game state and mode
   * @param gameKind - The game mode tag
   * @returns Translated game kind string
   */
  const parseGameKind = (gameKind: GameModeTag): string => {
    // Special cases take precedence over the game kind
    if (isCommunityGame) {
      return translate(GAME_TYPE.TRANSLATION.COMMUNITY)
    }

    if (isOpenTable) {
      return translate(GAME_TYPE.TRANSLATION.OPEN_TABLE)
    }

    const translationKey = GAME_TYPE.TRANSLATION.GAME_MODES[gameKind]
    return translate(translationKey || '')
  }

  /**
   * Gets the appropriate table name based on game mode
   * @param gameKind - The game mode tag
   * @returns Table name string
   */
  const getTableName = (gameKind: GameModeTag): string => {
    if (isCommunityGame) {
      return store?.game?.currentGameWithTournament?.name || tableName
    }

    return GAME_TYPE.TABLE_NAME[gameKind] || tableName
  }

  return (
    <View style={styles.actionsContainer}>
      <TableName name={getTableName(gameKind)} type={parseGameKind(gameKind)} />
      {isObserversVisible && (
        <DrawerLabelValue onPress={onObservers} label="Observers" value={observersCount.toString()} />
      )}
      {showShare && <DrawerLabelValue onPress={onShare} label="Share Game" value={hash} />}
    </View>
  )
})

export const DrawerHeaderItems = observer(() => {
  const router = useRouter()
  const store = useStore()
  const { isInGame, isMyFirstGame } = useIsInGame()
  const hash = store?.share?.info?.hash
  const isOpenTable = store.game?.isOpenTable ?? false
  const isTournamentModeOn = store.game?.isTournamentModeOn ?? false
  const userName = store.session?.user?.name?.slice(0, 15) ?? ''

  const { isSubscribed } = useStore().memberships

  const onViewProfile = useThrottledCallback(
    () => {
      router.push('/profile')
      requestAnimationFrame(() => {
        useDrawerStore.getState().closeDrawer()
      })
    },
    350,
    { leading: true }
  )

  if (isMyFirstGame) {
    return (
      <View style={[styles.actionsContainer, { borderBottomWidth: 0 }]}>
        <TableName name={'First Game'} subtitle={'Basics Table'} type={'WALK-THROUGH'} />
      </View>
    )
  }

  if (hash || isOpenTable || isTournamentModeOn) return <InGameHeaderItem />

  return (
    <TouchableOpacity onPress={onViewProfile}>
      <View style={styles.viewProfileContainer}>
        <View style={styles.avatar}>
          {isSubscribed && <Image style={styles.imageSize} source={CrownGradient} resizeMode="contain" />}
          <Avatar size={48} />
        </View>
        <Text style={styles.username}>{userName}</Text>
        <DS.Text variant="Caption/Regular">View Profile</DS.Text>
      </View>
    </TouchableOpacity>
  )
})

export const styles = StyleSheet.create({
  actionsContainer: {
    gap: DS.spacing[12],
    backgroundColor: 'transparent',
    paddingBottom: DS.spacing[20]
  },
  viewProfileContainer: {
    alignItems: 'flex-end',
    backgroundColor: 'transparent',
    gap: DS.spacing[8],
    paddingBottom: DS.spacing[20]
  },
  avatar: {
    position: 'relative',
  },
  username: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    color: color.palette.smokyBlack,
    fontSize: 20,
    lineHeight: 24
  },
  imageSize: {
    width: 30,
    height: 34,
    position: 'absolute',
    top: -24,
    left: 15,
    transform: [{ rotate: '14deg' }],
    overflow: 'visible'
  }
})
